import tlds from 'tlds';
import type { UserInitModel } from '~/common/models/api.models.ts';
import { useFacebookPixel } from '~/composables/facebookPixel.ts';
import { useOpenReplay } from '~/composables/openReplay.ts';
import { useMixpanel } from '~/composables/mixpanel.ts';
import { useCustomGtag } from '~/composables/gtag.ts';

export const EMAIL_REGEX = /^(?!.*\.\.)[\w-.]+@[\w-]+\.[a-z]{2,99}$/i;
export const SANDBOX_EMAIL_REGEX = /^(?!.*\.\.)[\w.+-]+@[\w-]+\.[a-z]{2,99}$/i;

export function useEmail(): any {
  const { ENVIRONMENT } = useRuntimeConfig().public;
  const store = useKegelStore();
  const API = useKegelApi();
  const router = useRouter();
  const route = useRoute();
  const localePath = useLocalePath();
  const { openError } = useAllModals();
  const { locale } = useI18n();
  const email = ref<string>('');
  const isLoading = ref(false);
  const showInputError = ref(false);
  const { openReplayTracker } = useOpenReplay();
  const { track } = useRedTrack();
  const { fbqTrack, pixelId } = useFacebookPixel();
  const { mixpanelTrack, getAnalyticsAttributes } = useMixpanel();
  const device = useDevice();
  const tiktokClickId = ref('');
  const {
    $db,
    $mixpanel,
    $versions,
    $snapTrack,
    $paymentMethods,
    $ttTrack,
  } = useNuxtApp();

  onMounted(async () => {
    tiktokClickId.value = await $db?.getItem('tiktok_click_id');
    await nextTick();
    store.analyticsAttributions = await getAnalyticsAttributes(route.query.rtkcid as string);
  });

  const emailPayload = computed((): UserInitModel => {
    const cookieClickId = useCookie('rtkclickid-store', {
      domain: window.location.hostname,
      path: '/',
      maxAge: 86400 * 90 * 1000,
    });
    const uuid_c1 = useCookie('_scid', {
      domain: window.location.hostname,
      path: '/',
      maxAge: 86400 * 90 * 1000,
    });

    return {
      email: email.value,
      platform: device?.isIos ? 'ios' : 'android',
      fbp: useCookie('_fbp').value,
      fbc: useCookie('_fbc').value || store.analyticsAttributions?.fbclid,
      ad_id: store.analyticsAttributions?.ad || route.query.sub1 as string,
      ad_placement: store.analyticsAttributions?.ad_placement || route.query.sub7 as string,
      ad_source: store.analyticsAttributions?.utm_source || route.query.utm_source as string,
      click_id: store.clickId || String(cookieClickId.value),
      click_time: store.analyticsAttributions?.click_time || '',
      tiktok_click_id: tiktokClickId.value || null,
      timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
      pixel: pixelId,
      twclid: route.query.twclid as string,
      snapchat_click_id: route.query.ScCid as string,
      snapchat_uuid_c1: uuid_c1.value as string,
      language: locale.value as string,
      age_group: route.query.age as string,
    };
  });

  function checkEmailFormat(email: string): boolean {
    if (ENVIRONMENT === 'development') {
      return SANDBOX_EMAIL_REGEX.test(email);
    }
    return EMAIL_REGEX.test(email);
  }

  async function useCheckDomainExists(email: string): Promise<boolean> {
    const domain = email?.split('@')[1]?.split('.')[1];

    if (!domain) {
      return false;
    }

    return tlds.includes(domain);
  }

  async function getHashedEmail(email: string): Promise<string> {
    const encoder = new TextEncoder();
    const data = encoder.encode(email.trim().toLowerCase());
    const hashBuffer = await crypto.subtle.digest('SHA-256', data);
    return Array.from(new Uint8Array(hashBuffer))
      .map(b => b.toString(16).padStart(2, '0'))
      .join('');
  }

  async function validateEmail(withRedirect = true): Promise<boolean> {
    isLoading.value = true;
    const isValid = await useCheckDomainExists(email.value);

    if (isValid) {
      await initUser(withRedirect);
      return true;
    } else {
      openError({ content: 'mail_error_message' });
      showInputError.value = true;
      isLoading.value = false;
      return false;
    }
  }

  async function initUser(withRedirect: boolean, inputEmail?: string): Promise<boolean> {
    try {
      if (inputEmail) {
        email.value = inputEmail;
      }

      await API.initUser(emailPayload.value);
      $db?.setItem('user_email', String(email.value));
      store.userEmail = email.value;
      store.sha256UserEmail = await getHashedEmail(store.userEmail);
      fbqTrack('CompleteRegistration');
      fbqTrack('Lead');
      await track('Lead');
      $ttTrack('CompleteRegistration');
      $ttTrack('Lead');
      $mixpanel?.people.set({ $email: email.value });
      openReplayTracker().setMetadata('email', store.userEmail);
      store.sha256UserEmail = await getHashedEmail(email.value);
      $snapTrack?.('SUBSCRIBE', { user_hashed_email: store.sha256UserEmail, user_email: email.value });
      if (withRedirect) {
        mixpanelTrack('E-mail Screen Completed', { email: email.value });
        goToNextPage(email.value);
      }

      return true;
    } catch (error: unknown) {
      console.error('INIT USER ERROR', error);
      isLoading.value = false;
      return false;
    }
  }

  async function authorizeUserInPaypal() {
    try {
      const payload = emailPayload.value;
      delete payload.email;
      await API.initUser(payload);
    } catch (error: unknown) {
      console.error('PP AUTH ERROR', error);
      isLoading.value = false;
    }
  }

  async function setEmailAfterPpPayment(email: string) {
    mixpanelTrack?.('Payment Email Retrieved', {
      payment_method: $paymentMethods?.paypal,
      email_attached: !!email,
    });
    $db?.setItem('user_email', String(email));
    store.userEmail = email;
    fbqTrack('CompleteRegistration');
    fbqTrack('Lead');
    await track('Lead');
    $mixpanel?.people.set({ $email: email });
    openReplayTracker().setMetadata('email', store.userEmail);
    store.sha256UserEmail = await getHashedEmail(email);
    $snapTrack?.('SUBSCRIBE', { user_hashed_email: store.sha256UserEmail, user_email: email });
  }

  function goToNextPage(email: string): void {
    const query = { ...route.query, user_uuid: store.userUuid, email };

    if ($versions.value.paymentForm === '3') {
      router.push(localePath({ path: '/landing-paddle', query }));
      return;
    }

    switch ($versions?.value.landing) {
      case '1':
        router.push(localePath({ path: '/landing', query }));
        break;
      default:
        router.push(localePath({ path: '/landing', query }));
    }
  }

  return {
    checkEmailFormat,
    useCheckDomainExists,
    getHashedEmail,
    validateEmail,
    email,
    initUser,
    showInputError,
    authorizeUserInPaypal,
    setEmailAfterPpPayment,
    isLoading,
  };
}
